import React from 'react';
import { Button, Icon, Typography } from '@saviynt/design-system';

import { useLocalize } from '../../../../../../shared/hooks';

import { DEFAULT_ACTIONS, type OverviewTitleProps } from './type';

import './OverviewTitle.css';

// Default action buttons for the overview section

/**
 * Displays the section title with action buttons for the License Details Overview tab.
 * Shows a license consumption title and provides configurable action buttons (e.g., Add License, Export).
 * @param {OverviewTitleProps} props - Component props
 * @param {string} [props.title] - Custom title text (defaults to "SAP RISE FUE License Consumption")
 * @param {ActionButton[]} [props.actions] - Array of action button configs (defaults to Add License and Export)
 * @param {() => void} [props.onAddLicenseClick] - Callback invoked when the Add License button is clicked
 * @returns {React.ReactElement} The overview title section with action buttons
 */
function OverviewTitle({
  title = 'SAP RISE FUE License Consumption',
  actions = DEFAULT_ACTIONS,
  onAddLicenseClick,
}: OverviewTitleProps): React.ReactElement {
  const handleActionClick = (action: (typeof DEFAULT_ACTIONS)[0]): void => {
    if (action.id === 'add-license' && onAddLicenseClick) {
      onAddLicenseClick();
    } else if (action.onClick) {
      action.onClick();
    }
  };

  const localize = useLocalize();

  return (
    <div className='LicenseManagement-OverviewTitle'>
      <div className='LicenseManagement-OverviewTitle-title'>
        <p className='LicenseManagement-OverviewTitle-titleText'>{title}</p>
      </div>
      <div className='LicenseManagement-OverviewTitle-actions'>
        {actions.map((action) => (
          <Button
            key={action.id}
            kind={action.kind}
            size='small'
            className='LicenseManagement-OverviewTitle-actionBtn'
            leftIcon={
              <Icon
                kind={action.icon}
                size='small'
                className='LicenseManagement-OverviewTitle-icon'
              />
            }
            onClick={() => handleActionClick(action)}>
            <Typography
              kind='body3-bold'
              className='LicenseManagement-OverviewTitle-label'>
              {localize(action.label)}
            </Typography>
          </Button>
        ))}
      </div>
    </div>
  );
}

export default OverviewTitle;
